<template>
  <div class="index container">
    <v-breadcrumbs class="d-print-none">
      <v-breadcrumbs-item :exact="true" :to="{ name: 'home', params: {} }"
        >Home</v-breadcrumbs-item
      >
      <v-breadcrumbs-divider>/</v-breadcrumbs-divider>
      <v-breadcrumbs-item
        :exact="true"
        :to="{ name: 'student', params: {} }"
        active
      >
        Students
      </v-breadcrumbs-item>
    </v-breadcrumbs>

    <div class="d-flex justify-space-between">
      <page-params
        pageName="studentSummary"
        vuexName="studentSummary"
        :parameters="parameters"
        :loadLatestOnMount="true"
      />
      <div class="d-print-none text-right mb-4">
        <v-btn @click="print">Print</v-btn>
        <v-btn @click="editColumns" class="ml-4">Edit Displays</v-btn>
      </div>
    </div>
    <v-divider class="d-print-none" />
    <div class="d-flex justify-center ma-2 text-h6">
      <div>
        Active Students Summary
        <div class="text-caption text-center">
          Found {{ studentList ? studentList.length : 0 }} active students
        </div>
      </div>
    </div>

    <div class="table-container">
      <v-data-table
        :headers="customHeaders"
        :items="filteredStudents"
        :loading="!studentProcessed"
        :disable-pagination="true"
        :hide-default-header="false"
        :hide-default-footer="true"
        :disable-sort="true"
        item-key="id"
        class="elevation-1"
        :options.sync="options"
        group-by="classroomId"
        :key="ts"
      >
        <template v-slot:group.header="{ items }">
          <th :colspan="customHeaders.length">
            {{
              items[0].classroomId
                ? items[0].classroomName
                : "Classroom Not Assigned"
            }}
            <span class="text-caption">({{ items.length }} Students)</span>
          </th>
        </template>

        <template v-slot:item.firstName="{ item }">
          <a @click="view(item)"
            >{{ item.firstName }}
            {{
              hideLastName && item.lastName
                ? item.lastName.charAt(0).toUpperCase()
                : item.lastName
            }}</a
          >
          <div class="text-caption font-weight-light">
            <span v-if="globalCenter && !globalCenter.id">
              {{ getCenterName(item.centerId) }} -
            </span>
            <span v-if="item.classroomName"> {{ item.classroomName }} - </span>
            <span>
              {{ item.contactTypeName }}
              <span v-if="!item.active"><v-divider vertical />(Inactive)</span>
            </span>
          </div>
        </template>

        <template v-slot:item.requirements="{ item }">
          <v-skeleton-loader v-if="loadingRequirement" type="list-item" />
          <div v-else>
            {{ getStudentRequirementStatus(item.id) }}
          </div>
        </template>

        <template v-slot:item.medicalAlerts="{ item }">
          <v-skeleton-loader v-if="loadingMedicalAlerts" type="list-item" />
          <div v-else class="text-subtitle-2 font-weight-light">
            <div
              v-for="value in getStudentMedicalAlerts(item.id)"
              :key="value.id"
            >
              {{ value.medicalAlertName }}
              <span v-if="value.medicalAlertOptionId">
                - {{ value.medicalAlertOptionName }}</span
              >
            </div>
          </div>
        </template>

        <template v-slot:item.dob="{ item }">
          {{ item.dob | formatDateYear }}
        </template>

        <template v-slot:item.lastUpdate="{ item }">
          {{ getLastUpdate(item.id) | formatDateYear }}
        </template>
      </v-data-table>
    </div>
    <confirmation-dialog ref="confirm" />
    <v-dialog persistent v-model="columnsDialog">
      <v-card class="pa-4">
        <v-card-text>
          <v-subheader>Displays</v-subheader>
          <v-divider class="mb-4" />
          <v-select
            v-model="parameters.includeColumns"
            :items="defaultColumns"
            label="Default Columns"
            item-text="text"
            item-value="value"
            multiple
            clearable
            chips
            outlined
            deletable-chips
          />
          <v-select
            label="Additional Columns (Requirements)"
            v-model="reportColumns"
            :items="requirementNames"
            item-text="fullName"
            item-value="id"
            return-object
            multiple
            clearable
            chips
            outlined
            hide-details
            deletable-chips
          />
          <v-checkbox
            v-model="hideLastName"
            :value="true"
            label="Hide last name"
          />

          <v-subheader v-if="classrooms && classrooms.length > 0"
            >Filters</v-subheader
          >
          <v-divider v-if="classrooms && classrooms.length > 0" class="mb-4" />
          <v-select
            v-if="classrooms && classrooms.length > 0"
            outlined
            label="Classroom(s)"
            v-model="selectedClassrooms"
            :items="classrooms"
            item-value="id"
            item-text="roomNumber"
            multiple
            clearable
            chips
          />
        </v-card-text>
        <v-card-actions class="justify-space-around">
          <v-btn color="primary" @click="updateStudentStatus">Done</v-btn>
          <v-btn @click="columnsDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";
import { mapActions, mapGetters } from "vuex";
import { getStudentRequirements } from "@/services/requirement";
import {
  fetchStudentsMedicalAlerts,
  groupMedicalAlertsByStudents,
} from "@/services/medicalAlertServices";
const ConfirmationDialog = () =>
  import("@/components/common/ConfirmationDialog.vue");
const PageParams = () => import("@/components/common/PageParams.vue");

export default {
  data() {
    return {
      options: {
        //define default sorting option here.
        sortBy: [],
        sortDesc: [],
      },
      ts: Date.now(),
      classrooms: [],
      selectedClassrooms: [],
      columnsDialog: false,
      loadingRequirement: true,
      loadingMedicalAlerts: true,
      studentList: [],
      requirementList: [],
      contactTypes: [],
      student2Requirements: new Map(),
      student2LastUpdate: new Map(),
      student2MedicalAlerts: {},
      requirementStatus: "All",
      activeOnly: false,
      hideLastName: false,
      headers: [
        {
          text: "Name",
          value: "firstName",
          align: "start",
          sortable: true,
        },
        {
          text: "DoB",
          value: "dob",
          align: "start",
          sortable: true,
        },
        {
          text: "Requirements",
          value: "requirements",
          align: "start",
          sortable: false,
        },
        {
          text: "Medical Alerts",
          value: "medicalAlerts",
          align: "start",
          sortable: false,
        },
        {
          text: "Memo",
          value: "memo",
          align: "start",
          sortable: false,
        },
        {
          text: "Last Update",
          value: "lastUpdate",
          align: "start",
          sortable: true,
        },
      ],
      defaultColumns: [
        {
          text: "Date of Birth",
          value: "dob",
        },
        {
          text: "Requirements",
          value: "requirements",
        },
        {
          text: "Medical Alerts",
          value: "medicalAlerts",
        },
        {
          text: "Memo",
          value: "memo",
        },
        {
          text: "Last Update",
          value: "lastUpdate",
        },
      ],
      sort: {
        key: null,
      },
    };
  },
  components: {
    ConfirmationDialog,
    PageParams,
  },
  computed: {
    ...mapGetters("student", ["studentProcessed", "getStudents"]),
    ...mapGetters("auth", ["myCenters"]),
    ...mapGetters("navigation", ["isAppLoading", "globalCenter"]),
    ...mapGetters("studentSummary", ["parameters"]),
    settings() {
      return this.parameters;
    },
    requirementNames() {
      const contactTypeIds = this.contactTypes.map((c) => c.id);
      if (this.requirementList) {
        const studentRequirements = this.requirementList.filter((r) => {
          if (contactTypeIds.includes(r.contactTypeId)) {
            return true;
          }
        });
        return Array.from(
          new Set(studentRequirements.map((r) => r.name))
        ).sort();
      } else {
        return [];
      }
    },
    reportColumns: {
      get() {
        return this.parameters.customColumns;
      },
      set(value) {
        this.parameters.customColumns = value;
        // this.setCustomColumns(value);
      },
    },
    customHeaders() {
      let customHeaders = [...this.headers];
      customHeaders = customHeaders.filter((header) =>
        this.parameters.includeColumns.includes(header.value)
      );

      if (
        this.parameters.customColumns &&
        this.parameters.customColumns.length > 0
      ) {
        this.parameters.customColumns.forEach((name) => {
          const header = {
            text: name,
            value: name,
            cellClass: "custom-cell",
            sortable: true,
          };
          customHeaders.splice(1, 0, header);
        });
      }

      return customHeaders;
    },
    filteredStudents() {
      if (this.selectedClassrooms && this.selectedClassrooms.length > 0) {
        return this.studentList.filter((student) => {
          return this.selectedClassrooms.includes(student.classroomId);
        });
      } else {
        return this.studentList;
      }
    },
  },
  methods: {
    ...mapActions("messages", [
      "addErrorMessage",
      "addMessage",
      "addSuccessMessage",
    ]),
    ...mapActions("student", ["deleteStudent", "fetchStudents"]),
    ...mapActions("classroom", ["fetchClassrooms"]),
    ...mapActions("navigation", ["hideDrawer"]),
    ...mapActions("requirement", ["fetchRequirements"]),
    ...mapActions("contactType", ["fetchContactTypes"]),
    // ...mapActions("studentSummary", ["setCustomColumns"]),

    getClassrooms() {
      //default to sorting by ID desc if not defined
      const sortBy = "roomNumber";
      const sortDesc = false;
      const params = {
        page: 0,
        size: 1000,
        sort: `${sortBy}${sortDesc ? ",desc" : ""}`,
      };
      this.fetchClassrooms(params)
        .then((response) => {
          if (response) {
            this.classrooms = response.content;
          }
        })
        .catch((error) => {
          this.addErrorMessage("Server response with error " + error);
        });
    },
    view(item) {
      this.$router.push({ name: "studentView", params: { id: item.id } });
    },
    initSummary() {
      this.loadingRequirement = true;
      this.loadingMedicalAlerts = true;
      const sortBy = "firstName";
      const sortDesc = false;
      debugger;
      const params = {
        page: 0,
        size: 10000,
        sort: `${sortBy}${sortDesc ? ",desc" : ""}`,
        searchTerms: null,
        activeOnly: true,
        classroomIds: null,
      };

      this.fetchStudents(params)
        .then((response) => {
          if (response) {
            this.studentList = response.content;

            const studentIds = this.studentList.map((s) => s.id);
            this.loadStudentRequirements(studentIds)
              .then((response) => {
                this.student2Requirements = response;
                this.updateStudentStatus();
                this.loadingRequirement = false;
              })
              .catch((error) => {
                console.error(error);
                this.addErrorMessage(
                  "Error loading requirement status for student"
                );
                this.loadingRequirement = false;
              });

            this.loadStudentMedicalAlerts(studentIds);
          }
        })
        .catch((error) => {
          this.loadingRequirement = false;
          this.addErrorMessage("Server response with error " + error);
        });
    },
    async loadStudentRequirements(studentIds) {
      const ids =
        studentIds && studentIds.length > 0 ? studentIds.join(",") : null;
      if (ids) {
        return getStudentRequirements(ids, true).then((response) => {
          const requirements = response.data;
          let student2Requirements = new Map();

          if (requirements && requirements.length > 0) {
            student2Requirements = requirements.reduce((map, r) => {
              const studentId = r.studentId;
              let completedCount = 0;
              let incompleteCount = 0;
              const statuses = r.status ? Object.values(r.status) : [];

              if (r.status) {
                completedCount = statuses.filter(
                  (s) => s.state == "completed" || s.state == "pending"
                ).length;
                incompleteCount = statuses.filter(
                  (s) => s.state == "incomplete"
                ).length;
              }

              if (map.has(studentId)) {
                map.get(studentId).completedCount += completedCount;
                map.get(studentId).incompleteCount += incompleteCount;
                map.get(studentId).requirements.push(r);
              } else {
                map.set(studentId, {
                  requirements: [r],
                  completedCount,
                  incompleteCount,
                });
              }

              // update last updated time student
              const lastUpdate = statuses.reduce((max, s) => {
                if (s.submissionDate) {
                  const lastUpdate = this.parseISOLocalDate(s.submissionDate);
                  if (max == null || (lastUpdate && max < lastUpdate)) {
                    max = lastUpdate;
                  }
                }
                return max;
              }, null);

              const studentLastUpdate = this.student2LastUpdate.get(studentId);
              if (
                studentLastUpdate == null ||
                (lastUpdate != null && studentLastUpdate < lastUpdate)
              ) {
                this.student2LastUpdate.set(studentId, lastUpdate);
              }

              return map;
            }, new Map());
          }

          return student2Requirements;
        });
      } else {
        return null;
      }
    },
    async loadStudentMedicalAlerts(studentIds) {
      const medicalAlerts = await fetchStudentsMedicalAlerts(studentIds);
      this.student2MedicalAlerts = await groupMedicalAlertsByStudents(
        medicalAlerts
      );

      this.loadingMedicalAlerts = false;
    },
    async loadContactTypes() {
      const params = { page: 0, size: 100, hasStudent: true, isCustomer: true };
      await this.fetchContactTypes(params).then((response) => {
        this.contactTypes = response.content;
      });
    },
    loadAvailableRequirements() {
      const sortBy = "name";
      const sortDesc = false;
      const params = {
        page: 0,
        size: 1000,
        sort: `${sortBy}${sortDesc ? ",desc" : ""}`,
        target: null,
      };
      this.fetchRequirements(params)
        .then((response) => {
          if (response) {
            this.requirementList = response.content;
          }
        })
        .catch((error) => {
          this.addErrorMessage("Server response with error " + error);
        });
    },
    getStudentRequirementStatus(studentId) {
      if (this.student2Requirements) {
        const status = this.student2Requirements.get(studentId);
        if (status) {
          const totalCount = status.incompleteCount + status.completedCount;
          return `${status.completedCount} of ${totalCount} requirements completed`;
        } else {
          return "n/a";
        }
      }
    },
    getStudentMedicalAlerts(studentId) {
      if (this.student2MedicalAlerts) {
        const studentAlert = this.student2MedicalAlerts[studentId];
        if (studentAlert) {
          return studentAlert.medicalAlerts;
        } else {
          return [];
        }
      }
    },
    getLastUpdate(studentId) {
      if (this.student2Requirements) {
        return this.student2LastUpdate.get(studentId);
      } else {
        return null;
      }
    },
    parseISOLocalDate(s) {
      var b = s.split(/\D/);
      return new Date(b[0], b[1] - 1, b[2]);
    },
    getCenterName(id) {
      if (this.myCenters && this.myCenters.length > 0) {
        const center = this.myCenters.find((c) => c.id == id);
        return center ? center.name : "Location N/A";
      } else {
        return "Location N/A";
      }
    },
    async print() {
      await this.hideDrawer();
      window.print();
    },
    editColumns() {
      this.loadContactTypes();
      this.loadAvailableRequirements();
      this.columnsDialog = true;
    },
    updateStudentStatus() {
      if (this.studentList && this.studentList.length > 0) {
        this.studentList.forEach((student) => {
          if (
            this.parameters.customColumns &&
            this.parameters.customColumns.length > 0
          ) {
            this.parameters.customColumns.forEach((requirementName) => {
              // Find requirements
              const studentRequirements = this.student2Requirements.get(
                student.id
              );
              const requirement = studentRequirements?.requirements?.find(
                (r) => r.name == requirementName
              );

              if (
                requirement?.status &&
                typeof requirement.status === "object"
              ) {
                let status = "";
                const keys = Object.keys(requirement.status);
                if (keys.length === 0) {
                  student[requirementName] = "n/a";
                  return;
                }
                keys.forEach((key) => {
                  const s = requirement.status[key];
                  const state =
                    s.state == "completed"
                      ? "Completed"
                      : s.state == "incomplete"
                      ? "Incomplete"
                      : "";

                  if (requirement.type === "Vaccination") {
                    status += `Dose ${key} `;
                  }

                  if (s.waived) {
                    status += "Waived";
                  } else {
                    if (
                      requirement.type == "Certification" &&
                      s.state == "completed"
                    ) {
                      const certificateExpiresOn = moment(s.cerfExpireDate);
                      const expired = certificateExpiresOn.isBefore(moment());
                      status += expired ? "Expired" : state;
                    } else {
                      status += state;
                    }
                  }

                  if (
                    requirement.type == "Certification" &&
                    s.state == "completed" &&
                    !s.waived
                  ) {
                    status += `\n${this.$options.filters.formatDateYear(
                      s.cerfIssueDate
                    )} - ${this.$options.filters.formatDateYear(
                      s.cerfExpireDate
                    )}`;
                  }
                  status += "\n";
                });
                student[requirementName] = status.trim(); // Remove trailing newline
              } else {
                student[requirementName] = "n/a";
              }
            });
          }
        });

        this.ts = Date.now();
        this.columnsDialog = false;
      }
    },
    // loadedSavedParams(){
    //   console.log('loaded page params...')
    //   this.updateStudentStatus();
    //   this.ts = Date.now();
    // }
  },
  mounted() {
    this.initSummary();
    this.getClassrooms();
  },
};
</script>

<style>
.custom-cell {
  white-space: pre !important;
}
</style>
